import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Headphones, Loader2, Plus, RefreshCw, Sparkles, Trash2, Volume2 } from 'lucide-react';
import { aiService } from '../../services/aiService';
import { useSimpleAudioGeneration } from '../../hooks/useSimpleAudioGeneration';
import { getAccessibleAudioUrl } from '../utils/audioUtils';
import { AVAILABLE_VOICES, VOICE_GROUPS, findVoiceForLanguage } from '../utils/voiceConstants';
import InstagramStoryMediaItem from './InstagramStoryMediaItem';
import { DEFAULT_INSTAGRAM_STORY_SCRIPT_INSTRUCTIONS } from './mediaPackageSchema';
import { createManualInstagramStory } from './instagramStoryDraft';
import { ensureCaptionHashtags, ensureExplicitAudioGuideCta, ensureStoryCaptionLink, finalizeSocialCaption } from './mediaPackageSocialCopy';

const AI_MODEL = 'gpt-5.6-sol';
const controlClass = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-fuchsia-500 focus:ring-2 focus:ring-fuchsia-100';

const cleanJsonResponse = (value) => String(value || '')
  .replace(/^```(?:json)?\s*/i, '')
  .replace(/\s*```$/i, '')
  .trim();

const cleanText = (value) => String(value || '').trim();
const wordCount = (value) => cleanText(value).split(/\s+/).filter(Boolean).length;
const StoryAudioPlayer = ({ url }) => {
  const [source, setSource] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let disposed = false;
    let generatedObjectUrl = '';
    setSource('');
    setError('');
    if (!url) return () => {};
    getAccessibleAudioUrl(url)
      .then((accessibleUrl) => {
        if (disposed) {
          if (String(accessibleUrl).startsWith('blob:')) URL.revokeObjectURL(accessibleUrl);
          return;
        }
        generatedObjectUrl = String(accessibleUrl).startsWith('blob:') ? accessibleUrl : '';
        setSource(accessibleUrl);
      })
      .catch((audioError) => setError(audioError.message || 'Audio preview could not be loaded.'));
    return () => {
      disposed = true;
      if (generatedObjectUrl) URL.revokeObjectURL(generatedObjectUrl);
    };
  }, [url]);

  if (error) return <p className="text-xs text-rose-700">{error}</p>;
  if (!source) return <div className="text-xs text-slate-500">Loading audio preview...</div>;
  return <audio controls preload="metadata" src={source} className="w-full" />;
};

const InstagramStorySeriesStudio = ({
  itinerary,
  packageData,
  onConfigChange,
  onError,
  workspaceId,
  campaignImageSource,
  partnerLogoSource,
  compact = false,
}) => {
  const [isGeneratingStories, setIsGeneratingStories] = useState(false);
  const [regeneratingStoryId, setRegeneratingStoryId] = useState('');
  const [generatingAudioId, setGeneratingAudioId] = useState('');
  const [selectedStoryIndex, setSelectedStoryIndex] = useState(0);
  const { generateAudio } = useSimpleAudioGeneration();
  const stories = Array.isArray(packageData.config.instagram_story_series)
    ? packageData.config.instagram_story_series
    : [];
  const outputLanguage = packageData.config.output_language || itinerary?.metadata?.language || 'en-US';
  const recommendedVoice = useMemo(() => findVoiceForLanguage(outputLanguage), [outputLanguage]);
  const selectedVoice = packageData.config.instagram_story_voice_id || recommendedVoice || 'en-US-Studio-O';
  const speechRate = packageData.config.instagram_story_speech_rate || '1.0';
  const activeStoryId = packageData.config.instagram_story_active_id || '';
  const storyScriptInstructions = packageData.config.instagram_story_script_instructions
    ?? DEFAULT_INSTAGRAM_STORY_SCRIPT_INSTRUCTIONS;

  const publicCampaignLink = packageData.config.short_url || packageData.config.public_guide_url || '';

  useEffect(() => {
    setSelectedStoryIndex((current) => Math.max(0, Math.min(current, stories.length - 1)));
  }, [stories.length]);

  const eligibleStops = useMemo(() => (
    (Array.isArray(itinerary?.points) ? itinerary.points : [])
      .filter((stop) => stop && stop.point_type !== 'popup_stop' && cleanText(stop.inDepthDescription))
      .map((stop, index) => ({
        stop_id: cleanText(stop.id) || `stop-${index + 1}`,
        stop_name: cleanText(stop.name || stop.title) || `Stop ${index + 1}`,
        approved_story: cleanText(stop.inDepthDescription).slice(0, 1800),
      }))
  ), [itinerary]);

  const replaceStories = (nextStories) => onConfigChange('instagram_story_series', nextStories);
  const addManualStory = () => {
    const story = createManualInstagramStory();
    replaceStories([story, ...stories]);
    setSelectedStoryIndex(0);
    onError?.('');
  };
  const hasRenderedPreview = (story) => Boolean(story?.rendered_video_url || story?.rendered_video_storage_path);
  const confirmPreviewInvalidation = (affectedStories) => {
    const renderedStories = affectedStories.filter(hasRenderedPreview);
    if (!renderedStories.length) return true;
    const label = renderedStories.length === 1
      ? `the saved MP4 preview for Story ${stories.findIndex((item) => item.id === renderedStories[0].id) + 1}`
      : `${renderedStories.length} saved Story MP4 previews`;
    return window.confirm(`This change will invalidate ${label}.\n\nThe source media and audio will remain available, but you will need to render the updated preview again. Continue?`);
  };
  const removeStory = (story) => {
    const renderedNote = hasRenderedPreview(story)
      ? '\n\nIts saved MP4 will no longer be included in the next published package.'
      : '';
    if (!window.confirm(`Remove “${story.title || story.stop_name || 'this Story'}” from the series?${renderedNote}`)) return;
    const remainingStories = stories.filter((candidate) => candidate.id !== story.id);
    replaceStories(remainingStories);
    if (activeStoryId === story.id) onConfigChange('instagram_story_active_id', '');
    setSelectedStoryIndex((current) => Math.max(0, Math.min(current, remainingStories.length - 1)));
  };
  const updateStory = (storyId, patch) => {
    const explicitlyInvalidates = patch.rendered_video_url === '' || patch.rendered_video_storage_path === '';
    const affectedStories = stories.filter((story) => story.id === storyId);
    if (explicitlyInvalidates && !confirmPreviewInvalidation(affectedStories)) return false;
    onConfigChange('instagram_story_series', (currentStories = []) => {
      const list = Array.isArray(currentStories) ? currentStories : [];
      return list.map((story) => story.id === storyId ? {
        ...story,
        ...patch,
        ...(explicitlyInvalidates ? { rendered_video_url: '', rendered_video_storage_path: '' } : {}),
      } : story);
    });
    return true;
  };

  const generateStoryDrafts = async () => {
    if (eligibleStops.length === 0) {
      onError?.('No stops have an inDepthDescription to turn into Instagram stories.');
      return;
    }
    if (!confirmPreviewInvalidation(stories)) return;
    setIsGeneratingStories(true);
    onError?.('');
    try {
      const targetCount = Math.min(5, eligibleStops.length);
      const prompt = [
        `Select the ${targetCount} strongest factual stories for a series of 30-second Instagram Story videos for this audio guide.`,
        `Write all public-facing text naturally in ${outputLanguage}.`,
        '',
        'CUSTOM STORY EDITORIAL BRIEF:',
        cleanText(storyScriptInstructions) || 'No additional creative instructions were supplied.',
        '',
        'SELECTION CRITERIA:',
        '- Prefer specific human stories, surprising turns, mysteries, conflicts, transformations, or vivid details.',
        '- Each selection must work independently for a first-time visitor with no prior context.',
        '- Choose varied stops and avoid five versions of the same theme.',
        '- Ignore logistics, generic introductions, and stops without a strong narrative hook.',
        '',
        'VOICE AND CLIENT SAFETY:',
        '- Write as Sofia: savvy, lightly cynical, deeply knowledgeable, conversational, and theatrical. Sofia hates formal “museum voice”.',
        '- This work must be sellable to and approvable by the museum or venue. Never ridicule, criticize, embarrass, or undermine the institution, client, collection, community, or subject.',
        '- Direct gentle irony only toward clearly supported human contradictions or decisions, and keep it appropriate for an institutional channel.',
        '- Speak in Sofia’s first-person narrative voice, but never pretend Sofia witnessed historical events or impersonate a historical person.',
        '- Write for the ear. If a sentence sounds like a brochure, rewrite it as natural speech.',
        '- Tell a story and communicate meaning. Do not recite lists, inventories, biographies, or plaque-style facts.',
        '- Use modern common sense sparingly to illuminate the human stakes, without imposing present-day political ideology.',
        '- Remain politically neutral. Mention political context only when the approved source makes it necessary.',
        '',
        '30-SECOND STORY STRUCTURE:',
        '- Narration should normally be 65 to 85 words and fit approximately 30 seconds. Quality matters more than reaching the upper limit.',
        '- Open in the middle of an action, contradiction, strong opinion, or vivid human moment. No greeting.',
        '- Introduce the place, object, person, or topic organically within the first few sentences.',
        '- Use dates as context rather than starting a sentence with a bare date.',
        '- Include one specific human, natural, or functional detail that gives the story meaning.',
        '- Reveal enough to be satisfying, then finish on a truthful unanswered question, consequence, or narrative turn.',
        '- The final sentence must be an explicit call to action asking the listener to download, open, or try the audio guide. Merely saying that Sofia tells the full story in the audio guide is not a call to action.',
        '',
        'STRICT WRITING RULES:',
        '- Use only the supplied inDepthDescription for factual claims. Do not add or infer facts.',
        '- Do not use: represents, stands as, testament to, must-see, stunning, breathtaking, hidden gem, flex, or “you won’t believe”.',
        '- Never begin with: Welcome to, You are standing at, Look at, Notice, See this, or Olha para.',
        '- Never use direct visual commands as an opening crutch.',
        '- Do not use em dashes or en dashes. Use commas, full stops, or colons.',
        '- Do not mention influencers, Instagram, TikTok, social media, or hashtags in the spoken narration.',
        '- Do not discuss present-day prices or costs.',
        '- Do not give walking directions.',
        '- Do not use formatting, labels, bullet points, stage directions, or quotation marks around the narration.',
        '- Use a powerful, honest curiosity gap. Never use fake suspense, unsupported superlatives, or claims the source cannot prove.',
        '- Return valid JSON only, with no markdown.',
        '',
        'OUTPUT JSON:',
        JSON.stringify({
          stories: [{
            stop_id: 'exact supplied stop_id',
            title: 'internal editorial title, maximum 8 words',
            visual_headline: 'on-screen hook, maximum 8 words',
            narration: 'complete 65-to-85-word spoken script including cliffhanger and audio-guide invitation',
            caption: `one concise Instagram caption with an explicit download/open CTA, the exact URL ${packageData.config.short_url || packageData.config.public_guide_url}, and these exact approved mentions: ${packageData.config.social_mentions || '@guide.sofia'}`,
          }],
        }, null, 2),
        '',
        'GUIDE:',
        JSON.stringify({
          title: packageData.config.guide_title,
          venue: packageData.config.venue,
          public_guide_url: packageData.config.public_guide_url,
          approved_social_mentions: packageData.config.social_mentions || '@guide.sofia',
        }, null, 2),
        '',
        'APPROVED STOPS:',
        JSON.stringify(eligibleStops, null, 2),
      ].join('\n');

      const response = await aiService.getCompletion([
        { role: 'system', content: 'You are Sofia, an institution-safe short-form audio storyteller and ruthless story editor. Find the most compelling truthful angle, create an immediate curiosity gap, and avoid brochure language. Return only valid JSON.' },
        { role: 'user', content: prompt },
      ], AI_MODEL, { temperature: 0.65, maxTokens: 8000, reasoningEffort: 'medium' });
      const parsed = JSON.parse(cleanJsonResponse(response));
      if (!Array.isArray(parsed.stories) || parsed.stories.length === 0) {
        throw new Error('The AI response did not contain story drafts.');
      }
      const stopById = new Map(eligibleStops.map((stop) => [stop.stop_id, stop]));
      const previousByStop = new Map(stories.map((story) => [story.stop_id, story]));
      const normalized = parsed.stories.slice(0, targetCount).map((story, index) => {
        const stop = stopById.get(cleanText(story.stop_id));
        if (!stop) throw new Error(`Story ${index + 1} did not reference a valid stop.`);
        const narration = ensureExplicitAudioGuideCta(story.narration, outputLanguage);
        if (!narration) throw new Error(`Story ${index + 1} did not include narration.`);
        const previous = previousByStop.get(stop.stop_id);
        const canPreserveAudio = previous?.audio_url && previous.audio_source_text === narration;
        return {
          id: previous?.id || `instagram-story-${stop.stop_id}`,
          stop_id: stop.stop_id,
          stop_name: stop.stop_name,
          title: cleanText(story.title) || stop.stop_name,
          visual_headline: cleanText(story.visual_headline) || cleanText(story.title) || stop.stop_name,
          visual_overtitle: previous?.visual_overtitle || '',
          visual_overtitle_color: previous?.visual_overtitle_color || '#57bfdc',
          visual_overtitle_size_percent: previous?.visual_overtitle_size_percent || 100,
          visual_headline_x_percent: previous?.visual_headline_x_percent ?? 7.5,
          visual_headline_y_percent: previous?.visual_headline_y_percent ?? 78,
          narration,
          caption: ensureCaptionHashtags(
            ensureStoryCaptionLink(finalizeSocialCaption(story.caption, {
              language: outputLanguage,
              url: publicCampaignLink,
              mentions: packageData.config.social_mentions || '@guide.sofia',
            }), publicCampaignLink, outputLanguage),
            packageData.config.hashtags
          ),
          audio_url: canPreserveAudio ? previous.audio_url : '',
          audio_duration: canPreserveAudio ? previous.audio_duration : 0,
          audio_source_text: canPreserveAudio ? narration : '',
          audio_stale: false,
          voice_id: canPreserveAudio ? previous.voice_id : '',
          media_url: previous?.media_url || '',
          media_storage_path: previous?.media_storage_path || '',
          media_content_type: previous?.media_content_type || '',
          media_fingerprint: previous?.media_fingerprint || '',
          media_filename: previous?.media_filename || '',
          image_overlay_url: previous?.image_overlay_url || '',
          image_overlay_storage_path: previous?.image_overlay_storage_path || '',
          image_overlay_content_type: previous?.image_overlay_content_type || '',
          image_overlay_fingerprint: previous?.image_overlay_fingerprint || '',
          image_overlay_filename: previous?.image_overlay_filename || '',
          image_overlay_disabled: previous?.image_overlay_disabled ?? false,
          image_overlay_size_percent: previous?.image_overlay_size_percent ?? 35,
          image_overlay_x_percent: previous?.image_overlay_x_percent ?? 50,
          image_overlay_y_percent: previous?.image_overlay_y_percent ?? 38,
          story_partner_logo_size_percent: previous?.story_partner_logo_size_percent ?? previous?.story_logo_size_percent ?? 16,
          story_partner_logo_content_scale_percent: previous?.story_partner_logo_content_scale_percent ?? 140,
          story_partner_logo_padding_percent: previous?.story_partner_logo_padding_percent ?? 0,
          story_partner_logo_x_percent: previous?.story_partner_logo_x_percent ?? 88,
          story_partner_logo_y_percent: previous?.story_partner_logo_y_percent ?? 9,
          story_ken_burns_direction: 'dramatic_push',
          story_ken_burns_zoom: 1.25,
          rendered_video_url: '',
          rendered_video_storage_path: '',
        };
      });
      replaceStories(normalized);
      if (activeStoryId && !normalized.some((story) => story.id === activeStoryId)) {
        onConfigChange('instagram_story_active_id', '');
      }
    } catch (generationError) {
      onError?.(generationError.message || 'The story series could not be generated.');
    } finally {
      setIsGeneratingStories(false);
    }
  };

  const regenerateSingleStory = async (story) => {
    const stop = eligibleStops.find((candidate) => candidate.stop_id === story.stop_id);
    if (!stop) {
      onError?.('The approved source for this Story is no longer available.');
      return;
    }
    setRegeneratingStoryId(story.id);
    onError?.('');
    try {
      const prompt = [
        'Create one genuinely fresh alternative 30-second Story from the approved stop below.',
        `Write all public-facing text naturally in ${outputLanguage}.`,
        '',
        'CUSTOM STORY EDITORIAL BRIEF:',
        cleanText(storyScriptInstructions) || 'No additional creative instructions were supplied.',
        '',
        '- Keep the same stop, but choose a different supported hook, narrative angle, opening, and wording from the current draft.',
        '- Use only the approved inDepthDescription for facts. Never invent or infer details.',
        '- Write as Sofia: savvy, lightly cynical, knowledgeable, conversational, theatrical, and safe for museum or client approval.',
        '- Tell a story in first person as Sofia, but never claim she witnessed historical events or impersonate a historical person.',
        '- Narration should normally be 65 to 85 words, written for the ear, with no greeting, lists, directions, formatting, or museum voice.',
        '- Open inside an action, contradiction, opinion, or vivid human moment. Introduce the subject naturally in the first few sentences.',
        '- Include one specific meaningful detail, then end on a truthful turn or cliffhanger.',
        '- The final sentence must explicitly ask the listener to download, open, or try the audio guide. Merely saying the story is in the audio guide is not a call to action.',
        '- Do not use em dashes or en dashes.',
        '- Do not mention social media, influencers, Instagram, TikTok, hashtags, or present-day prices in the narration.',
        '- Avoid: represents, stands as, testament to, must-see, stunning, breathtaking, hidden gem, flex, and you won\'t believe.',
        '- Never begin with Welcome to, You are standing at, Look at, Notice, See this, or Olha para.',
        `- The caption must include the exact audioguide URL ${packageData.config.short_url || packageData.config.public_guide_url} and these exact approved mentions: ${packageData.config.social_mentions || '@guide.sofia'}. Do not invent another URL or additional accounts.`,
        '- Return only valid JSON with no markdown.',
        '',
        'OUTPUT JSON:',
        JSON.stringify({
          story: {
            title: 'internal editorial title, maximum 8 words',
            visual_headline: 'on-screen hook, maximum 8 words',
            narration: 'complete 65-to-85-word spoken script',
            caption: 'concise caption ending with the exact approved mentions',
          },
        }, null, 2),
        '',
        'GUIDE:',
        JSON.stringify({
          title: packageData.config.guide_title,
          venue: packageData.config.venue,
          public_guide_url: packageData.config.public_guide_url,
        }, null, 2),
        '',
        'APPROVED STOP:',
        JSON.stringify(stop, null, 2),
        '',
        'CURRENT DRAFT TO AVOID REPEATING:',
        JSON.stringify({
          title: story.title,
          visual_headline: story.visual_headline,
          narration: story.narration,
          caption: story.caption,
        }, null, 2),
      ].join('\n');
      const response = await aiService.getCompletion([
        { role: 'system', content: 'You are Sofia, an institution-safe short-form audio storyteller and ruthless story editor. Find the most compelling truthful angle, create an immediate curiosity gap, and avoid brochure language. Return only valid JSON.' },
        { role: 'user', content: prompt },
      ], AI_MODEL, { temperature: 0.65, maxTokens: 3000, reasoningEffort: 'medium' });
      const parsed = JSON.parse(cleanJsonResponse(response));
      const alternative = parsed.story || parsed.stories?.[0];
      const narration = ensureExplicitAudioGuideCta(alternative?.narration, outputLanguage);
      if (!alternative || !narration) throw new Error('The AI response did not contain a replacement Story.');
      const replaced = updateStory(story.id, {
        title: cleanText(alternative.title) || story.title,
        visual_headline: cleanText(alternative.visual_headline) || cleanText(alternative.title) || story.visual_headline,
        narration,
        caption: ensureCaptionHashtags(
          ensureStoryCaptionLink(finalizeSocialCaption(alternative.caption, {
            language: outputLanguage,
            url: publicCampaignLink,
            mentions: packageData.config.social_mentions || '@guide.sofia',
          }), publicCampaignLink, outputLanguage),
          packageData.config.hashtags
        ),
        audio_url: '',
        audio_duration: 0,
        audio_source_text: '',
        audio_stale: false,
        voice_id: '',
        rendered_video_url: '',
        rendered_video_storage_path: '',
      });
      if (replaced && activeStoryId === story.id) onConfigChange('instagram_story_active_id', '');
    } catch (generationError) {
      onError?.(generationError.message || 'A new version of this Story could not be generated.');
    } finally {
      setRegeneratingStoryId('');
    }
  };

  const editNarration = (story, narration) => {
    const audioStale = Boolean(story.audio_url && story.audio_source_text !== narration);
    updateStory(story.id, { narration, audio_stale: audioStale, rendered_video_url: '' });
  };

  const editVisualHeadline = (story, visualHeadline, richSegments = null) => {
    const updated = updateStory(story.id, {
      visual_headline: visualHeadline,
      ...(richSegments ? { visual_headline_segments: richSegments } : {}),
      rendered_video_url: '',
    });
    if (!updated) return;
    if (activeStoryId === story.id) {
      onConfigChange('asset_copy', (currentAssetCopy = {}) => ({
        ...(currentAssetCopy || {}),
        instagram_story: {
          ...(currentAssetCopy?.instagram_story || {}),
          visual_headline: visualHeadline,
          ...(richSegments ? { visual_headline_segments: richSegments } : {}),
        },
      }));
    }
  };

  const selectAsSoundtrack = (story) => {
    onConfigChange('instagram_story_active_id', story.id);
    onConfigChange('asset_copy', (currentAssetCopy = {}) => ({
      ...(currentAssetCopy || {}),
      instagram_story: {
        ...(currentAssetCopy?.instagram_story || {}),
        visual_headline: story.visual_headline || story.title,
      },
    }));
  };

  const generateStoryAudio = async (story, index) => {
    if (!cleanText(story.narration)) {
      onError?.('Write or approve the narration before generating audio.');
      return;
    }
    setGeneratingAudioId(story.id);
    onError?.('');
    try {
      const result = await generateAudio(
        story.narration,
        { id: story.id, name: `instagram_story_${index + 1}_${story.stop_name}` },
        {
          ...(itinerary.metadata || {}),
          name: packageData.config.guide_title || itinerary.metadata?.name,
          language: outputLanguage,
        },
        selectedVoice,
        speechRate,
        {
          chunkingEnabled: false,
          geminiPrompt: 'Speak as Sofia: conversational, theatrical, savvy and lightly ironic, never formal or salesy. Sound institution-safe and genuinely curious. Keep a natural 30-second pace, land the truthful cliffhanger, and make the final audio-guide invitation clear.',
        }
      );
      updateStory(story.id, {
        audio_url: result.audioUrl,
        audio_duration: result.audioDuration || 0,
        audio_source_text: story.narration,
        audio_stale: false,
        voice_id: selectedVoice,
        rendered_video_url: '',
      });
    } catch (audioError) {
      onError?.(audioError.message || 'Story audio could not be generated.');
    } finally {
      setGeneratingAudioId('');
    }
  };

  return (
    <section className={compact ? '' : 'rounded-2xl border border-fuchsia-200 bg-fuchsia-50/40 p-5 shadow-sm'}>
      <div className={`flex flex-wrap items-start gap-4 ${compact ? 'justify-end' : 'justify-between'}`}>
        {!compact && <div>
          <div className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-fuchsia-600" /><h3 className="text-lg font-semibold text-slate-900">Instagram / Facebook Story series</h3></div>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600">Create independent 30-second Stories. AI generation selects up to five drafts, while manual Stories have no fixed limit. Each one keeps its own editable script, generated audio, image or video, preview, and MP4 download.</p>
        </div>}
        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" onClick={addManualStory} disabled={isGeneratingStories} className="flex items-center gap-2 rounded-lg border border-fuchsia-300 bg-white px-4 py-2 text-sm font-semibold text-fuchsia-700 hover:bg-fuchsia-50 disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400">
            <Plus className="h-4 w-4" />
            Add manual Story
          </button>
          <button type="button" onClick={generateStoryDrafts} disabled={isGeneratingStories} className="flex items-center gap-2 rounded-lg bg-fuchsia-600 px-4 py-2 text-sm font-semibold text-white disabled:bg-slate-400">
            {isGeneratingStories ? <Loader2 className="h-4 w-4 animate-spin" /> : stories.length ? <RefreshCw className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
            {isGeneratingStories ? 'Selecting stories...' : stories.length ? 'Regenerate five drafts' : 'Create five story drafts'}
          </button>
        </div>
      </div>

      <details className="mt-4 rounded-xl border border-fuchsia-200 bg-white">
        <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold text-slate-800">
          Edit Story script prompt
        </summary>
        <div className="border-t border-fuchsia-100 p-4">
          <p className="mb-3 text-sm leading-6 text-slate-600">
            This creative brief is saved with the Media Package and used both when creating the five drafts and when regenerating one Story. Factual and client-safety rules remain active.
          </p>
          <textarea
            className={`${controlClass} min-h-[280px] resize-y font-mono text-xs leading-5`}
            value={storyScriptInstructions}
            onChange={(event) => onConfigChange('instagram_story_script_instructions', event.target.value)}
            placeholder="Add the creative instructions for these Story scripts..."
          />
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-slate-500">Changes save automatically with this guide.</span>
            <button
              type="button"
              onClick={() => onConfigChange('instagram_story_script_instructions', DEFAULT_INSTAGRAM_STORY_SCRIPT_INSTRUCTIONS)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-fuchsia-300 bg-white px-3 py-2 text-xs font-semibold text-fuchsia-700 hover:bg-fuchsia-50"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Restore strong default
            </button>
          </div>
        </div>
      </details>

      <details className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
        <summary className="cursor-pointer text-sm font-semibold text-slate-700">Voice & pacing</summary>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Narration voice
          <select className={`${controlClass} mt-1`} value={selectedVoice} onChange={(event) => onConfigChange('instagram_story_voice_id', event.target.value)}>
            {VOICE_GROUPS.map((group) => (
              <optgroup key={group.language} label={group.language}>
                {group.voices.map((voiceId) => <option key={voiceId} value={voiceId}>{AVAILABLE_VOICES[voiceId]?.name || voiceId}</option>)}
              </optgroup>
            ))}
          </select>
        </label>
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Speech rate
          <input className="mt-3 w-full accent-fuchsia-600" type="range" min="0.75" max="1.35" step="0.05" value={speechRate} onChange={(event) => onConfigChange('instagram_story_speech_rate', event.target.value)} />
          <span className="mt-1 block text-xs font-normal normal-case text-slate-500">{speechRate}× (some Gemini voices use their own natural pace)</span>
        </label>
      </div>
      </details>
      <div className="mt-3 rounded-lg border border-fuchsia-200 bg-white px-4 py-2 text-sm text-slate-600"><span className="font-semibold text-fuchsia-700">{stories.length}</span> independent Story draft{stories.length === 1 ? '' : 's'}. Video duration follows each generated narration.</div>

      {stories.length > 0 && (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-fuchsia-200 bg-white p-3">
          <button type="button" aria-label="Previous Story" onClick={() => setSelectedStoryIndex((current) => (current - 1 + stories.length) % stories.length)} className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50"><ChevronLeft className="h-5 w-5" /></button>
          <div className="min-w-0 text-center">
            <div className="text-xs font-semibold uppercase tracking-wide text-fuchsia-700">Story {selectedStoryIndex + 1} of {stories.length}</div>
            <div className="mt-1 truncate text-sm font-medium text-slate-800">{stories[selectedStoryIndex]?.title || stories[selectedStoryIndex]?.stop_name}</div>
            <div className="mt-2 flex justify-center gap-1.5">
              {stories.map((story, index) => <button key={story.id} type="button" aria-label={`Open Story ${index + 1}`} onClick={() => setSelectedStoryIndex(index)} className={`h-2 rounded-full transition-all ${index === selectedStoryIndex ? 'w-6 bg-fuchsia-600' : 'w-2 bg-fuchsia-200 hover:bg-fuchsia-300'}`} />)}
            </div>
          </div>
          <button type="button" aria-label="Next Story" onClick={() => setSelectedStoryIndex((current) => (current + 1) % stories.length)} className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50"><ChevronRight className="h-5 w-5" /></button>
        </div>
      )}

      {stories.length === 0 ? (
        <div className="mt-5 rounded-xl border border-dashed border-fuchsia-300 bg-white p-6 text-center text-sm text-slate-600">No Story drafts yet. Add one manually or let the AI evaluate {eligibleStops.length} stop{eligibleStops.length === 1 ? '' : 's'} with approved in-depth content.</div>
      ) : (
        <div className="mt-5 space-y-5">
          {stories.slice(selectedStoryIndex, selectedStoryIndex + 1).map((story) => {
            const index = selectedStoryIndex;
            const isActive = activeStoryId === story.id;
            const words = wordCount(story.narration);
            return (
              <article key={story.id} className={`rounded-xl border bg-white p-4 ${isActive ? 'border-fuchsia-500 ring-2 ring-fuchsia-100' : 'border-slate-200'}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div><div className="text-xs font-semibold uppercase tracking-wide text-fuchsia-600">Story {index + 1} · {story.stop_name}</div><input className={`${controlClass} mt-1 font-semibold`} value={story.title || ''} onChange={(event) => updateStory(story.id, { title: event.target.value })} /></div>
                  <div className="flex flex-wrap gap-2">
                    {story.stop_id && <button type="button" disabled={regeneratingStoryId === story.id || isGeneratingStories} onClick={() => regenerateSingleStory(story)} className="flex items-center gap-1.5 rounded-lg border border-fuchsia-300 bg-white px-3 py-2 text-xs font-semibold text-fuchsia-700 disabled:bg-slate-100 disabled:text-slate-400">{regeneratingStoryId === story.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}{regeneratingStoryId === story.id ? 'Writing a new Story...' : 'Regenerate this Story'}</button>}
                    <button type="button" disabled={!story.audio_url || story.audio_stale} onClick={() => selectAsSoundtrack(story)} className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold ${isActive ? 'bg-fuchsia-600 text-white' : 'border border-slate-300 bg-white text-slate-700 disabled:bg-slate-100 disabled:text-slate-400'}`}>{isActive ? <Check className="h-3.5 w-3.5" /> : <Headphones className="h-3.5 w-3.5" />}{isActive ? 'Shown in main preview' : 'Use in main preview'}</button>
                    <button type="button" disabled={generatingAudioId === story.id} onClick={() => generateStoryAudio(story, index)} className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white disabled:bg-slate-400">{generatingAudioId === story.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Volume2 className="h-3.5 w-3.5" />}{generatingAudioId === story.id ? 'Generating...' : story.audio_url ? 'Regenerate audio' : 'Generate audio'}</button>
                    <button type="button" onClick={() => removeStory(story)} className="flex items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs font-semibold text-rose-700 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" />Remove Story</button>
                  </div>
                </div>

                <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(16rem,0.6fr)]">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Editable narration
                    <textarea className={`${controlClass} mt-1 min-h-[10rem] resize-y normal-case leading-6`} value={story.narration || ''} onChange={(event) => editNarration(story, event.target.value)} />
                    <span className={`mt-1 block text-xs font-normal normal-case ${words < 50 || words > 80 ? 'text-amber-700' : 'text-emerald-700'}`}>{words} words · target 55–75 for approximately 30 seconds</span>
                  </label>
                  <div className="space-y-3">
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Over-title colour<input className="mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white p-1" type="color" value={story.visual_overtitle_color || '#57bfdc'} onChange={(event) => updateStory(story.id, { visual_overtitle_color: event.target.value, rendered_video_url: '', rendered_video_storage_path: '' })} /></label>
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Caption<textarea className={`${controlClass} mt-1 min-h-[5rem] resize-y normal-case`} value={story.caption || ''} onChange={(event) => updateStory(story.id, { caption: event.target.value })} /></label>
                  </div>
                </div>

                {story.audio_url && (
                  <div className={`mt-4 rounded-lg border p-3 ${story.audio_stale ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
                    {story.audio_stale ? <p className="text-xs font-semibold text-amber-800">The narration changed after this audio was generated. Review the text, then regenerate before using it as a soundtrack.</p> : <StoryAudioPlayer url={story.audio_url} />}
                    {!story.audio_stale && story.voice_id && <p className="mt-1 text-xs text-slate-500">Voice: {AVAILABLE_VOICES[story.voice_id]?.name || story.voice_id}</p>}
                  </div>
                )}

                <div className="mt-4">
                  <InstagramStoryMediaItem
                    story={story}
                    index={index}
                    packageData={packageData}
                    workspaceId={workspaceId}
                    campaignImageSource={campaignImageSource}
                    partnerLogoSource={partnerLogoSource}
                    onUpdate={(patch) => updateStory(story.id, patch)}
                    onVisualHeadlineChange={(value, richSegments) => editVisualHeadline(story, value, richSegments)}
                    onError={onError}
                  />
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default InstagramStorySeriesStudio;
